/**
 * Gravitational Waves Visualization
 * 重力波の可視化 - 時空の歪み、ブラックホール連星系、LIGO検出
 */

import { WebGLApp } from '../WebGLApp'
import * as THREE from 'three'

interface BlackHole {
  position: THREE.Vector3
  mass: number
  mesh: THREE.Mesh
  orbitRadius: number
  orbitSpeed: number
  angle: number
}

export class GravitationalWaves extends WebGLApp {
  private blackHoles: BlackHole[] = []
  private spacetimeGrid: THREE.Points | null = null
  private waveRipples: THREE.Mesh[] = []
  private time: number = 0
  private waveAmplitude: number = 1.0
  private waveFrequency: number = 1.0
  
  // Physical constants
  private readonly SPEED_OF_LIGHT = 299792458 // m/s
  private readonly GRAVITATIONAL_CONSTANT = 6.67430e-11 // m³/kg⋅s²
  private readonly SOLAR_MASS = 1.989e30 // kg
  
  // Simulation parameters
  private readonly GRID_SIZE = 32
  private readonly WAVE_SPEED = 5.0
  private readonly SPACETIME_SIZE = 20
  
  protected createCamera(): THREE.PerspectiveCamera {
    return new THREE.PerspectiveCamera(75, this.width / this.height, 0.1, 1000)
  }

  protected async init(): Promise<void> {
    await this.initializeScene()
  }

  protected update(_deltaTime: number, _elapsedTime: number): void {
    this.updateAnimation()
  }

  protected async initializeScene(): Promise<void> {
    this.camera.position.set(25, 15, 25)
    this.camera.lookAt(0, 0, 0)
    
    // Setup lighting
    const ambientLight = new THREE.AmbientLight(0x202020, 0.3)
    this.scene.add(ambientLight)
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.7)
    directionalLight.position.set(15, 15, 15)
    this.scene.add(directionalLight)
    
    this.createBlackHoleBinary()
    this.createSpacetimeGrid()
    this.createWaveVisualization()
    this.createDetectors()
  }
  
  private createBlackHoleBinary(): void {
    // Create binary black hole system
    const blackHole1Mass = 30 * this.SOLAR_MASS
    const blackHole2Mass = 30 * this.SOLAR_MASS
    const separationDistance = 4.0
    
    // Black hole 1
    const bh1Geometry = new THREE.SphereGeometry(0.3, 16, 16)
    const bh1Material = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.9
    })
    const bh1Mesh = new THREE.Mesh(bh1Geometry, bh1Material)
    
    // Event horizon glow
    const bh1GlowGeometry = new THREE.SphereGeometry(0.5, 16, 16)
    const bh1GlowMaterial = new THREE.MeshBasicMaterial({
      color: 0x4444ff,
      transparent: true,
      opacity: 0.3
    })
    const bh1Glow = new THREE.Mesh(bh1GlowGeometry, bh1GlowMaterial)
    bh1Mesh.add(bh1Glow)
    
    this.scene.add(bh1Mesh)
    
    const blackHole1: BlackHole = {
      position: new THREE.Vector3(separationDistance / 2, 0, 0),
      mass: blackHole1Mass,
      mesh: bh1Mesh,
      orbitRadius: separationDistance / 2,
      orbitSpeed: 0.5,
      angle: 0
    }
    
    // Black hole 2
    const bh2Geometry = new THREE.SphereGeometry(0.3, 16, 16)
    const bh2Material = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.9
    })
    const bh2Mesh = new THREE.Mesh(bh2Geometry, bh2Material)
    
    const bh2GlowGeometry = new THREE.SphereGeometry(0.5, 16, 16)
    const bh2GlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xff4444,
      transparent: true,
      opacity: 0.3
    })
    const bh2Glow = new THREE.Mesh(bh2GlowGeometry, bh2GlowMaterial)
    bh2Mesh.add(bh2Glow)
    
    this.scene.add(bh2Mesh)
    
    const blackHole2: BlackHole = {
      position: new THREE.Vector3(-separationDistance / 2, 0, 0),
      mass: blackHole2Mass,
      mesh: bh2Mesh,
      orbitRadius: separationDistance / 2,
      orbitSpeed: 0.5,
      angle: Math.PI
    }
    
    this.blackHoles.push(blackHole1, blackHole2)
  }
  
  private createSpacetimeGrid(): void {
    // Create 3D spacetime grid to visualize warping
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(this.GRID_SIZE * this.GRID_SIZE * 3)
    const colors = new Float32Array(this.GRID_SIZE * this.GRID_SIZE * 3)
    
    let index = 0
    for (let i = 0; i < this.GRID_SIZE; i++) {
      for (let j = 0; j < this.GRID_SIZE; j++) {
        const x = (i / (this.GRID_SIZE - 1) - 0.5) * this.SPACETIME_SIZE
        const z = (j / (this.GRID_SIZE - 1) - 0.5) * this.SPACETIME_SIZE
        const y = 0
        
        positions[index * 3] = x
        positions[index * 3 + 1] = y
        positions[index * 3 + 2] = z
        
        // Color gradient
        colors[index * 3] = 0.3     // Red
        colors[index * 3 + 1] = 0.6 // Green
        colors[index * 3 + 2] = 1.0 // Blue
        
        index++
      }
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    
    const material = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
      transparent: true,
      opacity: 0.8
    })
    
    this.spacetimeGrid = new THREE.Points(geometry, material)
    this.scene.add(this.spacetimeGrid)
  }
  
  private createWaveVisualization(): void {
    // Create concentric ripples to show gravitational waves
    for (let i = 0; i < 10; i++) {
      const radius = 2 + i * 2
      const rippleGeometry = new THREE.TorusGeometry(radius, 0.05, 8, 64)
      const rippleMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.6 / (i + 1)
      })
      
      const ripple = new THREE.Mesh(rippleGeometry, rippleMaterial)
      ripple.rotation.x = Math.PI / 2
      ripple.userData = { 
        initialRadius: radius,
        phase: i * Math.PI / 5,
        waveNumber: i
      }
      
      this.scene.add(ripple)
      this.waveRipples.push(ripple)
    }
  }
  
  private createDetectors(): void {
    // Create LIGO-like detectors
    const detectorPositions = [
      new THREE.Vector3(8, 0, 8),
      new THREE.Vector3(-8, 0, -8),
      new THREE.Vector3(8, 0, -8)
    ]
    
    detectorPositions.forEach((pos, _index) => {
      // Detector base
      const baseGeometry = new THREE.BoxGeometry(0.5, 0.1, 0.5)
      const baseMaterial = new THREE.MeshLambertMaterial({ color: 0x444444 })
      const base = new THREE.Mesh(baseGeometry, baseMaterial)
      base.position.copy(pos)
      this.scene.add(base)
      
      // Detector arms (L-shaped)
      const arm1Geometry = new THREE.BoxGeometry(2, 0.05, 0.05)
      const arm2Geometry = new THREE.BoxGeometry(0.05, 0.05, 2)
      const armMaterial = new THREE.MeshLambertMaterial({ color: 0x00ff00 })
      
      const arm1 = new THREE.Mesh(arm1Geometry, armMaterial)
      const arm2 = new THREE.Mesh(arm2Geometry, armMaterial)
      
      arm1.position.set(pos.x + 1, pos.y + 0.1, pos.z)
      arm2.position.set(pos.x, pos.y + 0.1, pos.z + 1)
      
      this.scene.add(arm1)
      this.scene.add(arm2)
      
      // Store for strain visualization
      arm1.userData = { type: 'detector', axis: 'x', baseLength: 2 }
      arm2.userData = { type: 'detector', axis: 'z', baseLength: 2 }
    })
  }
  
  protected updateAnimation(): void {
    this.time += 0.016
    
    this.updateBlackHoleOrbit()
    this.updateSpacetimeWarping()
    this.updateGravitationalWaves()
    this.updateDetectorStrain()
  }
  
  private updateBlackHoleOrbit(): void {
    // Update black hole orbital motion
    this.blackHoles.forEach((bh, _index) => {
      // Inspiraling orbit (decreasing radius)
      const spiralRate = 0.99995 // Gradual inspiral
      bh.orbitRadius *= spiralRate
      
      // Increasing orbital frequency as they get closer
      const frequencyIncrease = 1.0001
      bh.orbitSpeed *= frequencyIncrease
      
      // Update position
      bh.angle += bh.orbitSpeed * 0.016
      bh.position.x = Math.cos(bh.angle) * bh.orbitRadius
      bh.position.z = Math.sin(bh.angle) * bh.orbitRadius
      
      bh.mesh.position.copy(bh.position)
      
      // Update wave parameters based on orbit
      this.waveFrequency = bh.orbitSpeed / (2 * Math.PI) * 2 // Twice orbital frequency
      this.waveAmplitude = 1.0 / Math.pow(bh.orbitRadius, 2) // Amplitude increases as they get closer
    })
  }
  
  private updateSpacetimeWarping(): void {
    if (!this.spacetimeGrid) return
    
    const positions = this.spacetimeGrid.geometry.attributes.position as THREE.BufferAttribute
    const posArray = positions.array as Float32Array
    
    // Calculate spacetime curvature around black holes
    for (let i = 0; i < posArray.length; i += 3) {
      const x = posArray[i]
      const z = posArray[i + 2]
      
      let totalWarp = 0
      
      // Calculate warping from each black hole
      this.blackHoles.forEach(bh => {
        const distance = Math.sqrt(
          (x - bh.position.x) ** 2 + (z - bh.position.z) ** 2
        )
        
        if (distance > 0.1) {
          // Simplified gravitational warping
          const warp = bh.mass / (this.SOLAR_MASS * distance * distance) * 0.0001
          totalWarp += warp
        }
      })
      
      // Apply gravitational wave distortion
      const wavePhase = this.waveFrequency * this.time - Math.sqrt(x*x + z*z) / this.WAVE_SPEED
      const waveStrain = this.waveAmplitude * Math.sin(wavePhase) * 0.1
      
      posArray[i + 1] = -totalWarp + waveStrain
    }
    
    positions.needsUpdate = true
  }
  
  private updateGravitationalWaves(): void {
    // Update wave ripples
    this.waveRipples.forEach(ripple => {
      const phase = this.waveFrequency * this.time + ripple.userData.phase
      const amplitude = this.waveAmplitude * Math.sin(phase)
      
      // Update ripple size and opacity based on wave
      const scale = 1 + amplitude * 0.1
      ripple.scale.setScalar(scale)
      
      // Update material opacity
      const material = ripple.material as THREE.MeshBasicMaterial
      material.opacity = Math.abs(amplitude) * 0.5 / (ripple.userData.waveNumber + 1)
      
      // Propagate outward
      ripple.userData.currentRadius = ripple.userData.initialRadius + this.time * this.WAVE_SPEED * 0.5
      
      // Reset when too far
      if (ripple.userData.currentRadius > 30) {
        ripple.userData.currentRadius = 2
      }
    })
  }
  
  private updateDetectorStrain(): void {
    // Calculate h+ and hx polarizations of gravitational waves
    const centerOfMass = new THREE.Vector3()
    this.blackHoles.forEach(bh => centerOfMass.add(bh.position))
    centerOfMass.divideScalar(this.blackHoles.length)
    
    // Find detector arms and apply strain
    this.scene.traverse(object => {
      if (object.userData.type === 'detector') {
        const mesh = object as THREE.Mesh
        const distance = mesh.position.distanceTo(centerOfMass)
        
        if (distance > 1) { // Only apply to distant detectors
          // Calculate strain (simplified)
          const wavePhase = this.waveFrequency * this.time - distance / this.WAVE_SPEED
          const strain = this.waveAmplitude * Math.sin(wavePhase) * 1e-21 * 1e15 // Amplified for visualization
          
          // Apply different strain to different axes (plus and cross polarizations)
          if (object.userData.axis === 'x') {
            mesh.scale.x = 1 + strain
            mesh.scale.z = 1 - strain
          } else if (object.userData.axis === 'z') {
            mesh.scale.z = 1 + strain
            mesh.scale.x = 1 - strain
          }
          
          // Color change to indicate detection
          const material = mesh.material as THREE.MeshLambertMaterial
          const intensity = Math.abs(strain) * 1000
          material.emissive.setRGB(intensity, intensity * 0.5, 0)
        }
      }
    })
  }
  
  public getWaveAmplitude(): number {
    return this.waveAmplitude
  }
  
  public getWaveFrequency(): number {
    return this.waveFrequency
  }
  
  public getBlackHoleSeparation(): number {
    if (this.blackHoles.length >= 2) {
      return this.blackHoles[0].position.distanceTo(this.blackHoles[1].position)
    }
    return 0
  }
  
  public dispose(): void {
    this.blackHoles.forEach(bh => {
      bh.mesh.geometry.dispose()
      if (bh.mesh.material instanceof THREE.Material) {
        bh.mesh.material.dispose()
      }
    })
    
    if (this.spacetimeGrid) {
      this.spacetimeGrid.geometry.dispose()
      if (this.spacetimeGrid.material instanceof THREE.Material) {
        this.spacetimeGrid.material.dispose()
      }
    }
    
    this.waveRipples.forEach(ripple => {
      ripple.geometry.dispose()
      if (ripple.material instanceof THREE.Material) {
        ripple.material.dispose()
      }
    })
    
    super.dispose()
  }
}